import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Users, Sprout, PenLine, X, Maximize2 } from "lucide-react";
import { preview } from "@/content/copy";
import { Eyebrow, Owl, Reveal } from "./primitives";

const ease = [0.22, 1, 0.36, 1];
const ICONS = { today: MessageCircle, community: Users, profile: Sprout, feedback: PenLine };

const Lightbox = ({ shot, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div data-testid="preview-lightbox" role="dialog" aria-modal="true" aria-label={`Enlarged screenshot: ${shot.title}`} className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-10 overflow-y-auto">
    <motion.div className="absolute inset-0 bg-[#0d2a99]/85" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} onClick={onClose} aria-hidden="true" />
    <motion.figure className="relative z-10 w-full max-w-md" initial={{ opacity: 0, y: 28, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.45, ease }}>
      <img src={shot.img} alt={shot.alt} className="w-full rounded-[1.8rem] border-4 border-[var(--cream)]/90 shadow-2xl" />
      <figcaption data-testid="preview-lightbox-caption" className="mt-4 text-center text-sm tracking-wide text-[var(--cream)]">{shot.label} — {shot.title}</figcaption>
    </motion.figure>
    <button type="button" autoFocus onClick={onClose} aria-label="Close enlarged screenshot" data-testid="preview-lightbox-close" className="absolute top-5 right-5 z-20 w-11 h-11 grid place-items-center rounded-full border border-[var(--rule)] bg-[var(--blue)]">
      <X size={20} />
    </button>
  </div>
  );
};

const Phone = ({ shot, onOpen, variant }) => (
  <div className={`ph-phone ${variant === "cream" ? "ph-phone-blue-bezel" : "ph-phone-cream-bezel"}`}>
    {shot.img ? (
      <button type="button" onClick={onOpen} data-testid={`preview-shot-${shot.key}`} className="ph-shot-btn" aria-haspopup="dialog" aria-label={`Enlarge screenshot: ${shot.title}`}>
        <img src={shot.img} alt={shot.alt} loading="lazy" className="ph-shot-img" />
        <span className="ph-shot-zoom" aria-hidden="true"><Maximize2 size={14} /> View larger</span>
      </button>
    ) : (
      <div className="ph-shot-pending" data-testid={`preview-pending-${shot.key}`}>
        <Owl alt="" className="w-14 h-14 object-contain" />
        <p>Prototype screenshot<br />coming soon</p>
      </div>
    )}
  </div>
);

export const Preview = () => {
  const [zoom, setZoom] = useState(null);
  const shot = zoom !== null ? preview.shots[zoom] : null;
  return (
    <section id="preview" data-testid="app-preview-section" className="ph-section scroll-mt-20">
      <div className="ph-container">
        <Reveal className="max-w-3xl">
          <Eyebrow>{preview.eyebrow}</Eyebrow>
          <h2 data-testid="preview-heading" className="ph-h2 mt-6">{preview.heading}</h2>
          <p data-testid="preview-intro" className="ph-lead mt-7">{preview.intro}</p>
        </Reveal>
        <div className="mt-14 sm:mt-20 grid gap-6 lg:gap-8">
          {preview.shots.map((s, i) => {
            const Icon = ICONS[s.key];
            const cream = i % 2 === 0;
            return (
              <Reveal key={s.key}>
                <article data-testid={`preview-card-${s.key}`} className={`ph-preview-card ${cream ? "ph-preview-cream" : "ph-preview-deep"} lg:grid lg:grid-cols-12 lg:gap-10`}>
                  <div className={`flex justify-center lg:row-start-1 ${cream ? "lg:col-span-5 lg:col-start-1 lg:justify-start" : "lg:col-span-5 lg:col-start-8 lg:justify-end"}`}>
                    <Phone shot={s} variant={cream ? "cream" : "deep"} onOpen={() => setZoom(i)} />
                  </div>
                  <div className={`mt-8 lg:mt-0 lg:row-start-1 ${cream ? "lg:col-span-6 lg:col-start-7" : "lg:col-span-6 lg:col-start-1"}`}>
                    <p className="ph-preview-label"><Icon size={15} strokeWidth={2.2} aria-hidden="true" />{s.label}</p>
                    <h3 data-testid={`preview-title-${s.key}`} className="ph-h3 mt-4">{s.title}</h3>
                    <p data-testid={`preview-text-${s.key}`} className="ph-body mt-5">{s.text}</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-12 sm:mt-16">
          <p data-testid="preview-status" className="ph-preview-status">{preview.status}</p>
          <p data-testid="preview-working-note" className="ph-note mt-6 max-w-2xl">
            <span className="ph-note-mark" aria-hidden="true">*</span>
            <span><strong className="font-semibold">{preview.workingLabel}:</strong> {preview.working}</span>
          </p>
        </Reveal>
      </div>
      <AnimatePresence>
        {shot?.img && <Lightbox shot={shot} onClose={() => setZoom(null)} />}
      </AnimatePresence>
    </section>
  );
};
